import React, { useState } from 'react';
import { Box, Button, Checkbox, Chip, FormControlLabel, IconButton, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TravelMode } from '../../types';
import { FixedPoint, MODE_LABELS, TripBasics, dateRange } from '../../lib/plan';

/**
 * Steps 1 and 2 of planning: where and when the trip starts and finishes, then
 * where and what for. Shared by the new-trip page and the board's "Trip basics"
 * dialog.
 *
 * Only the dates are required. Most trips are ground travel and the transport
 * is often decided a few days before, so "not decided" is a first-class answer
 * and station and time stay optional.
 */

export const THEME_PRESETS = ['history', 'nature', 'culture', 'food', 'art', 'architecture', 'hiking', 'wine'];

const MODES: TravelMode[] = ['train', 'flight', 'bus', 'car', 'ferry'];

export function emptyBasics(): TripBasics {
  return {
    title: '',
    destination: '',
    themes: [],
    brief: '',
    travelers: '',
    start_date: null,
    start_place: '',
    start_mode: null,
    start_detail: '',
    start_time: null,
    end_date: null,
    end_place: '',
    end_mode: null,
    end_detail: '',
    end_time: null,
    dates_precision: 'exact',
  };
}

/** "Tuscany · Nov 2026", from the destination and the start month. */
export function suggestTitle(b: TripBasics): string {
  const dest = b.destination?.trim();
  if (!dest) return '';
  if (!b.start_date) return dest;
  const month = new Date(`${b.start_date}T12:00:00Z`).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    year: 'numeric',
  });
  return `${dest} · ${month}`;
}

/** Null when the basics are enough to create a trip, else what is missing. */
export function basicsProblem(b: TripBasics): string | null {
  if (!b.destination?.trim()) return 'Where are you going?';
  if (!b.start_date) return 'When does the trip start?';
  if (!b.end_date) return 'When does it finish?';
  if (b.end_date < b.start_date) return 'The finish is before the start.';
  if (dateRange(b.start_date, b.end_date).length > 90) return 'That’s over 90 days. Check the year.';
  return null;
}

/** Empty strings → null, so blank fields don't land in the database as ''. */
export function cleanBasics(b: TripBasics): TripBasics {
  const n = (s: string | null) => (s && s.trim() ? s.trim() : null);
  return {
    ...b,
    title: b.title.trim() || suggestTitle(b) || 'Untitled trip',
    destination: n(b.destination),
    brief: n(b.brief),
    travelers: n(b.travelers),
    start_place: n(b.start_place),
    start_detail: n(b.start_detail),
    start_time: b.start_time || null,
    end_place: n(b.end_place),
    end_detail: n(b.end_detail),
    end_time: b.end_time || null,
  };
}

interface Props {
  value: TripBasics;
  onChange: (next: TripBasics) => void;
  /** Set once the traveller types a title, so it stops tracking the destination. */
  titleTouched: boolean;
  onTitleTouched: () => void;
  /** Fixed points are set when the trip is created; on the board they live on the days. */
  fixed?: FixedPoint[];
  onFixedChange?: (next: FixedPoint[]) => void;
}

export default function TripBasicsForm({ value, onChange, titleTouched, onTitleTouched, fixed, onFixedChange }: Props) {
  const [customTheme, setCustomTheme] = useState('');

  function set<K extends keyof TripBasics>(key: K, v: TripBasics[K]) {
    const next = { ...value, [key]: v };
    if (!titleTouched && (key === 'destination' || key === 'start_date')) next.title = suggestTitle(next);
    onChange(next);
  }

  function toggleTheme(t: string) {
    set('themes', value.themes.includes(t) ? value.themes.filter((x) => x !== t) : [...value.themes, t]);
  }

  function addCustomTheme() {
    const t = customTheme.trim().toLowerCase();
    if (t && !value.themes.includes(t)) set('themes', [...value.themes, t]);
    setCustomTheme('');
  }

  const extraThemes = value.themes.filter((t) => !THEME_PRESETS.includes(t));
  const nights = value.start_date && value.end_date && value.end_date >= value.start_date
    ? dateRange(value.start_date, value.end_date).length - 1
    : null;

  return (
    <Stack spacing={4}>
      <Box>
        <SectionLabel n={1} title="Start and finish" hint="From leaving home to getting back, or just the part you are planning. Only the dates are needed now; how you travel, the station and the time can wait." />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <EndFields
            label="Start"
            verb="travel"
            date={value.start_date}
            place={value.start_place}
            mode={value.start_mode}
            detail={value.start_detail}
            time={value.start_time}
            onDate={(v) => set('start_date', v)}
            onPlace={(v) => set('start_place', v)}
            onMode={(v) => set('start_mode', v)}
            onDetail={(v) => set('start_detail', v)}
            onTime={(v) => set('start_time', v)}
          />
          <EndFields
            label="Finish"
            verb="travel"
            date={value.end_date}
            min={value.start_date}
            place={value.end_place}
            mode={value.end_mode}
            detail={value.end_detail}
            time={value.end_time}
            onDate={(v) => set('end_date', v)}
            onPlace={(v) => set('end_place', v)}
            onMode={(v) => set('end_mode', v)}
            onDetail={(v) => set('end_detail', v)}
            onTime={(v) => set('end_time', v)}
          />
        </Box>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={value.dates_precision !== 'exact'}
              onChange={(e) => set('dates_precision', e.target.checked ? 'approximate' : 'exact')}
            />
          }
          label="These dates may still move"
        />

        {fixed && onFixedChange ? (
          <FixedPoints value={fixed} onChange={onFixedChange} start={value.start_date} end={value.end_date} />
        ) : null}

        {nights !== null ? (
          <Typography variant="body2" sx={{ mt: 2, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'rgba(26,58,42,0.07)', color: 'primary.main' }}>
            <strong>
              {nights + 1} {nights + 1 === 1 ? 'day' : 'days'}, {nights} {nights === 1 ? 'night' : 'nights'}
            </strong>
            {fixed?.filter((f) => f.town.trim()).length ? ` · ${fixed.filter((f) => f.town.trim()).length} fixed` : ''}
            {[value.start_mode, value.end_mode].filter((m) => !m).length
              ? ` · ${[value.start_mode, value.end_mode].filter((m) => !m).length} to decide later`
              : ''}
          </Typography>
        ) : null}
      </Box>

      <Box>
        <SectionLabel n={2} title="Where, and what for" />
        <Stack spacing={2}>
          <TextField
            label="Destination"
            placeholder="Tuscany, Japan, Guangdong …"
            value={value.destination ?? ''}
            onChange={(e) => set('destination', e.target.value)}
            required
            fullWidth
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Themes
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[...THEME_PRESETS, ...extraThemes].map((t) => {
                const on = value.themes.includes(t);
                return (
                  <Chip
                    key={t}
                    label={t}
                    onClick={() => toggleTheme(t)}
                    color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'}
                  />
                );
              })}
              <TextField
                size="small"
                placeholder="Add your own"
                value={customTheme}
                onChange={(e) => setCustomTheme(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomTheme();
                  }
                }}
                onBlur={addCustomTheme}
                sx={{ width: 150 }}
              />
            </Box>
          </Box>
          <TextField
            label="Travellers"
            placeholder="Two adults, one who hates early starts"
            value={value.travelers ?? ''}
            onChange={(e) => set('travelers', e.target.value)}
            helperText="Inviting them to follow the plan comes soon."
            fullWidth
          />
          <TextField
            label="Anything else"
            placeholder="Been to Florence before. Want one proper hike. No car."
            value={value.brief ?? ''}
            onChange={(e) => set('brief', e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label="Trip name"
            value={value.title}
            onChange={(e) => {
              onTitleTouched();
              onChange({ ...value, title: e.target.value });
            }}
            helperText={titleTouched ? undefined : 'Filled in from the destination and month until you change it.'}
            fullWidth
          />
        </Stack>
      </Box>
    </Stack>
  );
}

interface EndProps {
  label: string;
  verb: string;
  date: string | null;
  min?: string | null;
  place: string | null;
  mode: TravelMode | null;
  detail: string | null;
  time: string | null;
  onDate: (v: string | null) => void;
  onPlace: (v: string) => void;
  onMode: (v: TravelMode | null) => void;
  onDetail: (v: string) => void;
  onTime: (v: string | null) => void;
}

function EndFields(p: EndProps) {
  return (
    <Box
      component="fieldset"
      sx={{ m: 0, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}
    >
      <Typography component="legend" variant="body2" sx={{ px: 0.75, fontWeight: 700, color: 'primary.main' }}>
        {p.label}
      </Typography>
      <TextField
        label="Date"
        type="date"
        value={p.date ?? ''}
        onChange={(e) => p.onDate(e.target.value || null)}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: p.min ?? undefined } }}
        required
      />
      <TextField label="Where" placeholder="Town or city" value={p.place ?? ''} onChange={(e) => p.onPlace(e.target.value)} />
      <Box>
        <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
          How you {p.verb}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          <Chip
            label="Not decided"
            onClick={() => p.onMode(null)}
            color={p.mode === null ? 'primary' : 'default'}
            variant={p.mode === null ? 'filled' : 'outlined'}
          />
          {MODES.map((m) => (
            <Chip
              key={m}
              label={MODE_LABELS[m]}
              onClick={() => p.onMode(m)}
              color={p.mode === m ? 'primary' : 'default'}
              variant={p.mode === m ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Box>
      {p.mode ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
          <TextField
            label={p.mode === 'flight' ? 'Airport or flight' : p.mode === 'car' ? 'Where from' : 'Station or stop'}
            value={p.detail ?? ''}
            onChange={(e) => p.onDetail(e.target.value)}
          />
          <TextField
            label="Time"
            type="time"
            value={p.time?.slice(0, 5) ?? ''}
            onChange={(e) => p.onTime(e.target.value || null)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Optional"
          />
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Station and time appear once you choose how. It’ll be on your Decide later list until then.
        </Typography>
      )}
    </Box>
  );
}

function FixedPoints({
  value,
  onChange,
  start,
  end,
}: {
  value: FixedPoint[];
  onChange: (next: FixedPoint[]) => void;
  start: string | null;
  end: string | null;
}) {
  const update = (i: number, patch: Partial<FixedPoint>) => onChange(value.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography sx={{ fontWeight: 600 }}>Fixed points along the way</Typography>
        <Typography variant="caption" color="text.secondary">
          Optional
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        A place you’ve committed to on a date before the rest is planned, like being in Shenzhen on 19 Oct and choosing air
        or train a few days before.
      </Typography>
      <Stack spacing={1.25}>
        {value.map((f, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr 44px', sm: '2fr 1fr 44px' }, gap: 1.25, alignItems: 'center' }}>
            <TextField size="small" label="Be in" value={f.town} onChange={(e) => update(i, { town: e.target.value })} />
            <TextField
              size="small"
              label="On"
              type="date"
              value={f.date}
              onChange={(e) => update(i, { date: e.target.value })}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: start ?? undefined, max: end ?? undefined } }}
            />
            <IconButton aria-label="Remove fixed point" onClick={() => onChange(value.filter((_, j) => j !== i))}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        size="small"
        sx={{ mt: 1.5, borderStyle: 'dashed' }}
        disabled={!start || !end}
        onClick={() => onChange([...value, { town: '', date: start ?? '' }])}
      >
        + Add a fixed point
      </Button>
    </Box>
  );
}

function SectionLabel({ n, title, hint }: { n: number; title: string; hint?: string }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 700 }}>
        {n} · {title}
      </Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}
